import React from "react";
import { ArrowRight, CheckCircle2, ShieldCheck, Sparkles, Tag } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";

const proofPoints = [
  "Kostnadsfri TakKoll",
  "ROT-avdrag direkt på fakturan",
  "10 års garanti på takmålning",
];

export default function Hero() {
  return (
    <section id="top" className="relative isolate overflow-hidden bg-[#070e24]">
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <img src="/hero-aerial.webp" alt="" className="h-full w-full object-cover opacity-35" />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(7,14,36,0.98)_0%,rgba(7,14,36,0.94)_42%,rgba(7,14,36,0.64)_100%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(7,14,36,0.28),rgba(7,14,36,0.9))]" />
      </div>

      <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-5 pb-16 pt-11 sm:px-6 sm:pb-20 sm:pt-16 lg:grid-cols-[0.94fr_1.06fr] lg:gap-14 lg:pb-24 lg:pt-20">
        <div className="reveal max-w-2xl">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-2 rounded-full bg-[#38BDF8] px-4 py-2 text-xs font-extrabold uppercase tracking-[0.08em] text-[#062a4f] shadow-[0_12px_30px_rgba(56,189,248,0.25)]" data-testid="hero-campaign-badge">
              <Tag className="h-4 w-4" /> 20% kampanjrabatt
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-bold text-white" data-testid="hero-service-area">
              <Sparkles className="h-4 w-4 text-[#38BDF8]" /> Stockholm &amp; Uppsala
            </span>
          </div>

          <p className="mt-7 text-sm font-extrabold uppercase tracking-[0.16em] text-[#38BDF8]" data-testid="hero-eyebrow">High Performance Roofing</p>
          <h1 className="mt-3 text-4xl font-extrabold leading-[1.04] text-white sm:text-5xl lg:text-6xl" data-testid="hero-heading">
            Rädda taket innan
            <span className="block text-[#38BDF8]">du behöver byta det.</span>
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-200 sm:text-lg" data-testid="hero-description">
            HPR ger ditt tak nytt liv med noggrann taktvätt, takmålning och takbehandling. Börja med en kostnadsfri TakKoll och få ett tydligt fast pris.
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Button asChild className="h-14 rounded-full bg-[#1B45C4] px-7 text-base font-bold text-white shadow-[0_16px_32px_rgba(27,69,196,0.35)] transition-transform hover:-translate-y-0.5 hover:bg-[#2953d6]">
              <a href="#kontakt" data-testid="hero-quote-cta">Få kostnadsfri offert <ArrowRight className="ml-2 h-5 w-5" /></a>
            </Button>
            <Button asChild variant="outline" className="h-14 rounded-full border-white/30 bg-white/5 px-7 text-base font-bold text-white transition-colors hover:border-[#38BDF8] hover:bg-white/10 hover:text-white">
              <a href="/boka-takkoll" data-testid="hero-takkoll-cta">Boka kostnadsfri TakKoll</a>
            </Button>
          </div>

          <ul className="mt-9 grid gap-x-6 gap-y-3 sm:grid-cols-2" data-testid="hero-proof-list">
            {proofPoints.map((point) => (
              <li key={point} className="flex items-center gap-2 text-sm font-semibold text-slate-100" data-testid={`hero-proof-${point.toLowerCase().replaceAll(" ", "-").replaceAll("å", "a").replaceAll("ö", "o")}`}>
                <CheckCircle2 className="h-5 w-5 shrink-0 text-[#38BDF8]" /> {point}
              </li>
            ))}
          </ul>
        </div>

        <div className="reveal relative pt-4 lg:pt-0" data-testid="hero-project-visual">
          <div className="absolute -inset-5 -z-10 bg-[#38BDF8]/15 blur-3xl" aria-hidden="true" />
          <div className="overflow-hidden rounded-2xl border border-white/15 bg-[#0d1b40] shadow-[0_30px_70px_rgba(0,0,0,0.4)]">
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3 sm:px-5">
              <div className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.12em] text-white">
                <span className="h-2 w-2 rounded-full bg-[#38BDF8]" /> Ett HPR-projekt
              </div>
              <span className="text-xs font-semibold text-slate-300" data-testid="hero-project-caption">Taktvätt · före &amp; efter</span>
            </div>
            <div className="relative aspect-[1.49/1] overflow-hidden">
              <img src="/taktvatt-split.jpg" alt="Före och efter från ett HPR-projekt med taktvätt" className="h-full w-full object-cover" fetchPriority="high" />
              <div className="pointer-events-none absolute inset-y-0 left-1/2 w-0.5 bg-[#38BDF8] shadow-[0_0_18px_rgba(56,189,248,0.95)]" aria-hidden="true" />
              <span className="absolute left-4 top-4 rounded-full bg-[#071333]/90 px-3 py-1.5 text-xs font-extrabold uppercase tracking-[0.12em] text-white" data-testid="hero-before-label">Före</span>
              <span className="absolute right-4 top-4 rounded-full bg-[#38BDF8] px-3 py-1.5 text-xs font-extrabold uppercase tracking-[0.12em] text-[#062a4f]" data-testid="hero-after-label">Efter</span>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-4 sm:px-5">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#38BDF8]/15"><ShieldCheck className="h-5 w-5 text-[#38BDF8]" /></span>
                <span><strong className="block text-sm text-white">Tydligt resultat. Tydlig process.</strong><span className="text-xs text-slate-300">TakKoll först · fast pris innan start</span></span>
              </div>
              <Link to="/referenser" data-testid="hero-projects-link" className="inline-flex items-center gap-1 text-sm font-bold text-[#38BDF8] transition-colors hover:text-white">Se projekt <ArrowRight className="h-4 w-4" /></Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}